import { Fragment, type ReactNode } from 'react';

// Splits plain message text into text + link + mention runs so a pasted
// URL is tappable and an "@nickname" reads as a mention. Matches http(s)://
// and bare www. URLs; trailing sentence punctuation (.,;:!?) and an
// unbalanced closing bracket/quote are left out of the link so "see
// https://zmist.co." doesn't capture the period and "(https://zmist.co)"
// doesn't capture the paren. Deliberately conservative - no autolinking of
// bare domains or emails, which produce more false positives than they're
// worth here.
const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;
const TRAILING_PUNCT_RE = /[.,;:!?]+$/;

// Mirrors the backend's mentionCandidateRe (modules/signal/transitions/
// signal.go) - same charset, same "preceded by a boundary char" guard so
// "me@zmist.co" is never mistaken for a mention. This is purely a visual
// highlight (it doesn't know which @names resolve to real channel
// members - that check only happens server-side, see ResolveMentions) so
// it renders a styled span, not a link.
const MENTION_RE = /(^|[\s(\[{,;:])@([A-Za-z0-9][A-Za-z0-9_.\-]{0,31})/g;

export type LinkifiedSegment =
  | { type: 'text'; value: string }
  | { type: 'link'; value: string; href: string; trailing: string }
  | { type: 'mention'; value: string; nickname: string };

// Manual slicing (matchAll + index math) rather than String.split, so the
// boundary character captured by MENTION_RE stays exactly where it was
// instead of being consumed by a capturing-group split - the same reason
// trimTrailing below hand-slices instead of relying on the regex alone.
function splitMentions(text: string): LinkifiedSegment[] {
  if (!text) return [];
  const segments: LinkifiedSegment[] = [];
  let lastIndex = 0;
  for (const m of text.matchAll(MENTION_RE)) {
    const [full, boundary, nickname] = m;
    const start = m.index ?? 0;
    const before = text.slice(lastIndex, start + boundary.length);
    if (before) segments.push({ type: 'text', value: before });
    segments.push({ type: 'mention', value: '@' + nickname, nickname });
    lastIndex = start + full.length;
  }
  const rest = text.slice(lastIndex);
  if (rest) segments.push({ type: 'text', value: rest });
  return segments;
}

function trimTrailing(url: string): { url: string; trailing: string } {
  let trailing = '';
  let out = url;
  const unbalancedClose = /[)\]}"']$/;
  while (unbalancedClose.test(out)) {
    const close = out[out.length - 1];
    const open = close === ')' ? '(' : close === ']' ? '[' : close === '}' ? '{' : close;
    const opens = out.split(open).length - 1;
    const closes = out.split(close).length - 1;
    // A quote (open === close) or a genuinely unbalanced bracket gets
    // trimmed; a bracket that's balanced within the match stays.
    if (open !== close && closes <= opens) break;
    trailing = close + trailing;
    out = out.slice(0, -1);
  }
  const m = out.match(TRAILING_PUNCT_RE);
  if (m) {
    trailing = m[0] + trailing;
    out = out.slice(0, -m[0].length);
  }
  return { url: out, trailing };
}

export function splitLinkified(text: string): LinkifiedSegment[] {
  if (!text) return [];
  // String.split with a capturing group interleaves [text, match, text, ...].
  const parts = text.split(URL_RE);
  const segments: LinkifiedSegment[] = [];
  parts.forEach((part, i) => {
    if (!part) return;
    if (i % 2 === 0) {
      segments.push(...splitMentions(part));
      return;
    }
    const { url, trailing } = trimTrailing(part);
    if (!url) {
      segments.push({ type: 'text', value: part });
      return;
    }
    const href = url.startsWith('www.') ? `https://${url}` : url;
    segments.push({ type: 'link', value: url, href, trailing });
  });
  return segments;
}

// Shared by LinkifiedText and MarkdownReportText.tsx so a report's bold/
// header/bullet lines get the exact same link/mention rendering an
// ordinary message does, from one place.
export function renderLinkifiedSegments(segments: LinkifiedSegment[], keyPrefix: string | number = ''): ReactNode {
  return segments.map((seg, i) =>
    seg.type === 'text' ? (
      <Fragment key={`${keyPrefix}-${i}`}>{seg.value}</Fragment>
    ) : seg.type === 'mention' ? (
      <span key={`${keyPrefix}-${i}`} className="text-emerald-600 dark:text-emerald-400 font-medium">
        {seg.value}
      </span>
    ) : (
      <Fragment key={`${keyPrefix}-${i}`}>
        <a
          href={seg.href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          // Message bubbles are themselves clickable (open the detail
          // sheet) - a tap on the link should only follow the link.
          onClick={(e) => e.stopPropagation()}
          className="text-blue-600 dark:text-blue-400 underline break-all"
        >
          {seg.value}
        </a>
        {seg.trailing}
      </Fragment>
    )
  );
}

interface LinkifiedTextProps {
  text: string;
  className?: string;
}

export function LinkifiedText({ text, className }: LinkifiedTextProps): ReactNode {
  return <span className={className}>{renderLinkifiedSegments(splitLinkified(text))}</span>;
}
